// iter-121.117 — OA Action Drawers
//
// Mirror of `cockpit/ActionDrawer.jsx` but every endpoint is OA-scoped
// (org pinned server-side). Row data and inline write actions reuse
// existing platform endpoints (resend OTP from auth_routes,
// cancel/resend invite from auth_routes, etc.).
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ExternalLink, RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";
import { useOaScope } from "@/lib/oa-scope";

function timeFromNow(iso) {
    if (!iso) return "—";
    const diff = (new Date(iso).getTime() - Date.now()) / 1000;
    const sign = diff < 0 ? "-" : "";
    const abs = Math.abs(diff);
    if (abs < 3600) return `${sign}${Math.round(abs / 60)}m`;
    if (abs < 86400) return `${sign}${Math.round(abs / 3600)}h`;
    return `${sign}${Math.round(abs / 86400)}d`;
}

function DrawerTable({ columns, rows, emptyMsg, testidPrefix }) {
    if (!rows || rows.length === 0) {
        return (
            <div className="text-xs text-muted-foreground py-8 text-center"
                 data-testid={`${testidPrefix}-empty`}>
                {emptyMsg || "No rows."}
            </div>
        );
    }
    return (
        <div className="border border-border rounded-sm overflow-hidden">
            <table className="w-full text-[12px]" data-testid={`${testidPrefix}-table`}>
                <thead className="bg-secondary/40 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    <tr>
                        {columns.map((c) => (
                            <th key={c.key} className={`text-left px-2 py-1.5 ${c.align === "right" ? "text-right" : ""}`}>
                                {c.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={r.connector_id || r.invitation_id || r.user_id || r.tenant_id || i}
                            className="border-t border-border"
                            data-testid={`${testidPrefix}-row-${i}`}>
                            {columns.map((c) => (
                                <td key={c.key}
                                    className={`px-2 py-1.5 ${c.align === "right" ? "text-right" : ""}`}>
                                    {c.render ? c.render(r) : (r[c.key] ?? "—")}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function useOaDrawerData(key, queryString) {
    const [rows, setRows] = useState(null);
    const [reloadAt, setReloadAt] = useState(0);
    useEffect(() => {
        setRows(null);
        api.get(`/home/oa/actions/${key}?${queryString || ""}`)
            .then((r) => setRows(r.data?.rows || []))
            .catch(() => setRows([]));
    }, [key, queryString, reloadAt]);
    const reload = useCallback(() => setReloadAt(Date.now()), []);
    return [rows, reload];
}

function DrawerHeader({ onReload, onNavigateAll, label }) {
    return (
        <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                {label || "Detail rows"}
            </div>
            <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={onReload}
                        className="h-7 px-2" data-testid="oa-drawer-reload">
                    <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reload
                </Button>
                {onNavigateAll && (
                    <Button size="sm" variant="outline" onClick={onNavigateAll}
                            className="h-7 px-2" data-testid="oa-drawer-open-all">
                        Open full view <ExternalLink className="h-3.5 w-3.5 ml-1" />
                    </Button>
                )}
            </div>
        </div>
    );
}

// ── Schema mappings ────────────────────────────────────────────────────
function SchemaMappingDrawer({ onNavigate }) {
    const { queryString } = useOaScope();
    const [rows, reload] = useOaDrawerData("schema-mapping-pending", queryString);
    const columns = [
        { key: "connector_name", label: "Connector",
          render: (r) => <span className="font-medium">{r.connector_name}</span> },
        { key: "tenant_name", label: "Tenant" },
        { key: "source_type", label: "Type",
          render: (r) => <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{r.source_type}</span> },
        { key: "reason", label: "Reason",
          render: (r) => <span className="text-[11px] text-muted-foreground">{r.reason}</span> },
        { key: "_actions", label: "", align: "right",
          render: (r) => (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                      onClick={() => onNavigate(`/data?connector=${r.connector_id}&action=map`)}
                      data-testid={`oa-map-${r.connector_id}`}>
                  Map fields
              </Button>
          ),
        },
    ];
    return (
        <>
            <DrawerHeader onReload={reload}
                onNavigateAll={() => onNavigate("/data?tab=connectors")}
                label="Connectors awaiting field mapping" />
            {rows === null
                ? <div className="text-xs text-muted-foreground py-4">Loading connectors…</div>
                : <DrawerTable columns={columns} rows={rows}
                               emptyMsg="No connectors need field mapping."
                               testidPrefix="oa-schema-mapping" />}
        </>
    );
}

// ── AI Budget warnings ─────────────────────────────────────────────────
function AiBudgetDrawer({ onNavigate }) {
    const { queryString } = useOaScope();
    const [rows, reload] = useOaDrawerData("ai-budget-warning", queryString);
    const columns = [
        { key: "tenant_name", label: "Tenant",
          render: (r) => <span className="font-medium">{r.tenant_name}</span> },
        { key: "used_pct", label: "Used", align: "right",
          render: (r) => {
              const tone = r.used_pct >= 100 ? "text-destructive"
                         : r.used_pct >= 90 ? "text-amber-600 dark:text-amber-400"
                         : "text-foreground";
              return <span className={`font-mono tabular-nums ${tone}`}>{r.used_pct}%</span>;
          },
        },
        { key: "used", label: "Used / Cap", align: "right",
          render: (r) => (
              <span className="text-[11px] text-muted-foreground">
                  {(r.used || 0).toLocaleString()} / {(r.cap || 0).toLocaleString()} cr
              </span>
          ),
        },
        { key: "cap_source", label: "Source",
          render: (r) => (
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                  {r.cap_source === "tenant_override" ? "override" : `plan · ${r.plan_slug || "?"}`}
              </span>
          ),
        },
        { key: "_actions", label: "", align: "right",
          render: (r) => (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                      onClick={() => onNavigate(`/settings/ai-budgets?tenant=${r.tenant_id}`)}
                      data-testid={`oa-budget-${r.tenant_id}`}>
                  Adjust
              </Button>
          ),
        },
    ];
    return (
        <>
            <DrawerHeader onReload={reload}
                onNavigateAll={() => onNavigate("/settings/ai-budgets")}
                label="Tenants ≥ 80% of monthly AI credit cap" />
            {rows === null
                ? <div className="text-xs text-muted-foreground py-4">Loading budgets…</div>
                : <DrawerTable columns={columns} rows={rows}
                               emptyMsg="All tenants well under their AI cap."
                               testidPrefix="oa-ai-budget" />}
        </>
    );
}

// ── Connectors failing ─────────────────────────────────────────────────
function ConnectorsFailingDrawer({ onNavigate }) {
    const { queryString } = useOaScope();
    const [rows, reload] = useOaDrawerData("connectors-failing", queryString);
    const columns = [
        { key: "connector_name", label: "Connector",
          render: (r) => <span className="font-medium">{r.connector_name}</span> },
        { key: "tenant_name", label: "Tenant" },
        { key: "source_type", label: "Type",
          render: (r) => <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{r.source_type}</span> },
        { key: "last_sync_at", label: "Failed", align: "right",
          render: (r) => <span className="text-destructive">{timeFromNow(r.last_sync_at)}</span> },
        { key: "_actions", label: "", align: "right",
          render: (r) => (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                      onClick={() => onNavigate(`/data?connector=${r.connector_id}`)}
                      data-testid={`oa-conn-diagnose-${r.connector_id}`}>
                  Diagnose
              </Button>
          ),
        },
    ];
    const drilldown = rows && rows[0] ? (
        <div className="mt-2 text-[11px] text-muted-foreground italic">
            Latest error: <span className="text-foreground">{rows[0].last_error}</span>
        </div>
    ) : null;
    return (
        <>
            <DrawerHeader onReload={reload}
                onNavigateAll={() => onNavigate("/data?tab=connectors")}
                label="Connectors that failed in the last 24h" />
            {rows === null
                ? <div className="text-xs text-muted-foreground py-4">Loading connectors…</div>
                : (
                    <>
                        <DrawerTable columns={columns} rows={rows}
                                     emptyMsg="No connector failures in the last 24h."
                                     testidPrefix="oa-connectors-failing" />
                        {drilldown}
                    </>
                )}
        </>
    );
}

// ── Pending invites ────────────────────────────────────────────────────
function PendingInvitesDrawer({ onNavigate }) {
    const { queryString } = useOaScope();
    const [rows, reload] = useOaDrawerData("pending-invites", queryString);
    const resend = async (invitationId, email) => {
        try {
            await api.post(`/auth/invite/${invitationId}/resend`);
            toast.success(`Invitation resent to ${email}`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to resend invite");
        }
    };
    const cancel = async (invitationId, email) => {
        if (!window.confirm(`Cancel pending invite to ${email}?`)) return;
        try {
            await api.delete(`/auth/invite/${invitationId}`);
            toast.success(`Invitation cancelled for ${email}`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to cancel invite");
        }
    };
    const columns = [
        { key: "email", label: "Email",
          render: (r) => <span className="font-medium">{r.email}</span> },
        { key: "role", label: "Role",
          render: (r) => <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{r.role}</span> },
        { key: "tenant_name", label: "Tenant" },
        { key: "created_at", label: "Sent", align: "right",
          render: (r) => <span className="text-muted-foreground">{timeFromNow(r.created_at)}</span> },
        { key: "_actions", label: "", align: "right",
          render: (r) => (
              <div className="flex items-center gap-1 justify-end">
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                          onClick={() => resend(r.invitation_id, r.email)}
                          data-testid={`oa-invite-resend-${r.invitation_id}`}>
                      Resend
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-destructive"
                          onClick={() => cancel(r.invitation_id, r.email)}
                          data-testid={`oa-invite-cancel-${r.invitation_id}`}>
                      Cancel
                  </Button>
              </div>
          ),
        },
    ];
    return (
        <>
            <DrawerHeader onReload={reload}
                onNavigateAll={() => onNavigate("/team")}
                label="Outstanding team-member invitations" />
            {rows === null
                ? <div className="text-xs text-muted-foreground py-4">Loading invites…</div>
                : <DrawerTable columns={columns} rows={rows}
                               emptyMsg="No pending invites."
                               testidPrefix="oa-pending-invites" />}
        </>
    );
}

// ── Stalled signups ────────────────────────────────────────────────────
function StalledSignupsDrawer({ onNavigate }) {
    const { queryString } = useOaScope();
    const [rows, reload] = useOaDrawerData("stalled-signups", queryString);
    const resendOtp = async (userId, email) => {
        try {
            const r = await api.post(`/home/oa/actions/resend-otp/${userId}`);
            if (r.data?.ok) toast.success(`OTP resent to ${email}`);
            else toast.error(r.data?.message || "Failed to resend OTP");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to resend OTP");
        }
    };
    const columns = [
        { key: "email", label: "Email",
          render: (r) => <span className="font-medium">{r.email}</span> },
        { key: "tenant_name", label: "Tenant" },
        { key: "created_at", label: "Joined", align: "right",
          render: (r) => <span className="text-muted-foreground">{timeFromNow(r.created_at)}</span> },
        { key: "_actions", label: "", align: "right",
          render: (r) => (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                      onClick={() => resendOtp(r.user_id, r.email)}
                      data-testid={`oa-signup-resend-${r.user_id}`}>
                  <Mail className="h-3 w-3 mr-1" /> Resend OTP
              </Button>
          ),
        },
    ];
    return (
        <>
            <DrawerHeader onReload={reload}
                onNavigateAll={() => onNavigate("/team")}
                label="Users unverified for > 24h" />
            {rows === null
                ? <div className="text-xs text-muted-foreground py-4">Loading users…</div>
                : <DrawerTable columns={columns} rows={rows}
                               emptyMsg="No stalled signups."
                               testidPrefix="oa-stalled-signups" />}
        </>
    );
}

// ── Trial ending soon ──────────────────────────────────────────────────
function TrialEndingDrawer({ onNavigate }) {
    const { queryString } = useOaScope();
    const [rows, reload] = useOaDrawerData("trial-ending", queryString);
    const columns = [
        { key: "tenant_name", label: "Tenant",
          render: (r) => <span className="font-medium">{r.tenant_name}</span> },
        { key: "industry", label: "Industry",
          render: (r) => <span className="text-[11px] text-muted-foreground capitalize">{r.industry || "—"}</span> },
        { key: "users_count", label: "Users", align: "right" },
        { key: "trial_ends_at", label: "Ends", align: "right",
          render: (r) => <span className="text-destructive">{timeFromNow(r.trial_ends_at)}</span> },
        { key: "_actions", label: "", align: "right",
          render: (r) => (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                      onClick={() => onNavigate(`/billing?tenant=${r.tenant_id}&action=upgrade`)}
                      data-testid={`oa-trial-upgrade-${r.tenant_id}`}>
                  Choose plan
              </Button>
          ),
        },
    ];
    return (
        <>
            <DrawerHeader onReload={reload}
                onNavigateAll={() => onNavigate("/billing")}
                label="Tenants whose trial ends within 3 days" />
            {rows === null
                ? <div className="text-xs text-muted-foreground py-4">Loading trials…</div>
                : <DrawerTable columns={columns} rows={rows}
                               emptyMsg="No trials ending soon."
                               testidPrefix="oa-trial-ending" />}
        </>
    );
}

const RENDERERS = {
    schema_mapping_pending: SchemaMappingDrawer,
    ai_budget_warning:      AiBudgetDrawer,
    connectors_failing:     ConnectorsFailingDrawer,
    pending_invites:        PendingInvitesDrawer,
    stalled_signups:        StalledSignupsDrawer,
    trial_ending_soon:      TrialEndingDrawer,
};

export default function OaActionDrawer({ item, onClose, onNavigate }) {
    const Renderer = RENDERERS[item.key];
    if (!Renderer) {
        return (
            <div className="text-xs text-muted-foreground py-8 text-center"
                 data-testid="oa-action-drawer-unknown">
                No detail view available for `{item.key}`.
            </div>
        );
    }
    return <Renderer onClose={onClose} onNavigate={onNavigate} />;
}
